import { Center, MantineColor, Tooltip, TooltipProps, UnstyledButton } from '@mantine/core';
import { useClickOutside, useHotkeys } from '@mantine/hooks';
import QuestionMark from 'components/QuestionMark';
import { useState } from 'react';

/**
 * Parameters of the QuestionMarkTooltip component.
 */
export interface QuestionMarkTooltipProps extends Omit<TooltipProps, 'children'> {
  size?: number;
  color?: MantineColor;
  buttonLabel?: string;
  buttonTabIndex?: number;
}

/** Optional button trigger that isolates help from a surrounding form control. */
function ButtonQuestionMarkTooltip({
  size,
  color,
  buttonLabel,
  buttonTabIndex,
  opened,
  ...props
}: QuestionMarkTooltipProps) {
  const [active, setActive] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setActive(false));
  useHotkeys([['Escape', () => setActive(false), { preventDefault: false }]], []);

  return (
    <Tooltip {...props} opened={opened || active}>
      <UnstyledButton
        ref={ref}
        type="button"
        aria-label={buttonLabel}
        tabIndex={buttonTabIndex}
        w={size}
        h={size}
        style={{ display: 'inline-flex', flexShrink: 0 }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          setActive(true);
        }}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      >
        <QuestionMark size={size} color={color} />
      </UnstyledButton>
    </Tooltip>
  );
}

/**
 * Displays a question mark icon with a tooltip.
 */
export default function QuestionMarkTooltip({
  size = 16,
  color = 'blue.7',
  buttonLabel,
  buttonTabIndex,
  ...props
}: QuestionMarkTooltipProps) {
  if (buttonLabel) {
    return (
      <ButtonQuestionMarkTooltip
        size={size}
        color={color}
        buttonLabel={buttonLabel}
        buttonTabIndex={buttonTabIndex}
        {...props}
      />
    );
  }
  return (
    <Tooltip {...props}>
      <Center inline w={size} h={size}>
        <QuestionMark size={size} color={color} />
      </Center>
    </Tooltip>
  );
}
